"use client";

import { c as _c } from "react/compiler-runtime";
import { IconAlertCircle, IconBellPlus, IconRosetteDiscountCheck } from "@tabler/icons-react";
import { skipToken, useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { useCallback, useTransition } from "react";
import { useIsClient } from "@/hooks/use-is-client";
import { useRouter } from "@/hooks/use-router";
import { useTRPC } from "@/lib/trpc/client";
import { useSession } from "@domainstack/auth/client";
import { Button } from "@domainstack/ui/button";
import { Spinner } from "@domainstack/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";
type TrackDomainButtonProps = {
  domain: string;
  /** Whether the button is enabled. Defaults to true. */
  enabled?: boolean;
};
function TrackedVerifiedButton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Tooltip><TooltipTrigger render={<Button variant="outline" nativeButton={false} aria-label="View in dashboard" render={<Link href="/dashboard"><IconRosetteDiscountCheck className="text-success-foreground" aria-hidden="true" /><span className="sr-only">View in dashboard</span></Link>} />} /><TooltipContent>You own and track this domain</TooltipContent></Tooltip>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function TrackOrVerifyButton(t0) {
  const $ = _c(23);
  const {
    isPendingVerification,
    isAuthenticated,
    isNavigating,
    onClick
  } = t0;
  const label = isPendingVerification ? "Verify" : "Track";
  let t1;
  if ($[0] !== isPendingVerification) {
    t1 = isPendingVerification ? <IconAlertCircle className="text-accent-orange" aria-hidden="true" /> : <IconBellPlus className="sm:text-muted-foreground" aria-hidden="true" />;
    $[0] = isPendingVerification;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const icon = t1;
  let t2;
  if ($[2] !== icon || $[3] !== isNavigating) {
    t2 = isNavigating ? <Spinner /> : icon;
    $[2] = icon;
    $[3] = isNavigating;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== label) {
    t3 = <span className="hidden sm:inline">{label}</span>;
    $[5] = label;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const buttonContent = t4;
  const tooltipText = isPendingVerification ? "Complete verification for this domain" : "Get alerts for this domain";
  const ariaLabel = isPendingVerification ? "Verify domain" : "Track domain";
  let t5;
  if ($[10] !== ariaLabel || $[11] !== buttonContent || $[12] !== isAuthenticated || $[13] !== isNavigating || $[14] !== onClick) {
    t5 = isAuthenticated ? <Button variant="outline" onClick={onClick} disabled={isNavigating} aria-label={ariaLabel}>{buttonContent}</Button> : <Button variant="outline" nativeButton={false} aria-label={ariaLabel} render={<Link href="/login" scroll={false}>{buttonContent}</Link>} />;
    $[10] = ariaLabel;
    $[11] = buttonContent;
    $[12] = isAuthenticated;
    $[13] = isNavigating;
    $[14] = onClick;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const trigger = t5;
  let t6;
  if ($[16] !== trigger) {
    t6 = <TooltipTrigger render={trigger} />;
    $[16] = trigger;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== tooltipText) {
    t7 = <TooltipContent><p>{tooltipText}</p></TooltipContent>;
    $[18] = tooltipText;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t6 || $[21] !== t7) {
    t8 = <Tooltip>{t6}{t7}</Tooltip>;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
export function TrackDomainButton(t0) {
  const $ = _c(17);
  const {
    domain,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const {
    data: session,
    isPending: isSessionPending
  } = useSession();
  const router = useRouter();
  const trpc = useTRPC();
  const [isNavigating, startNavigation] = useTransition();
  const mounted = useIsClient();
  const isAuthenticated = !!session?.user;
  let t2;
  if ($[0] !== domain || $[1] !== isAuthenticated || $[2] !== trpc.tracking.getTrackingStatus) {
    t2 = trpc.tracking.getTrackingStatus.queryOptions(isAuthenticated ? {
      domain
    } : skipToken);
    $[0] = domain;
    $[1] = isAuthenticated;
    $[2] = trpc.tracking.getTrackingStatus;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: trackedDomain,
    isLoading: isLoadingStatus
  } = useQuery(t2);
  const isTracked = !!trackedDomain;
  const isVerified = trackedDomain?.verified ?? false;
  const isPendingVerification = isTracked && !isVerified;
  let t3;
  if ($[4] !== domain || $[5] !== isPendingVerification || $[6] !== router || $[7] !== session?.user || $[8] !== trackedDomain) {
    t3 = () => {
      if (!session?.user) {
        return;
      }
      if (isPendingVerification && trackedDomain) {
        const params = new URLSearchParams({
          resume: "true",
          id: trackedDomain.id
        });
        if (trackedDomain.verificationMethod) {
          params.set("method", trackedDomain.verificationMethod);
        }
        startNavigation(() => router.push(`/dashboard/add-domain?${params.toString()}`, {
          scroll: false
        }));
        return;
      }
      startNavigation(() => router.push(`/dashboard/add-domain?domain=${encodeURIComponent(domain)}`, {
        scroll: false
      }));
    };
    $[4] = domain;
    $[5] = isPendingVerification;
    $[6] = router;
    $[7] = session?.user;
    $[8] = trackedDomain;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  session?.user;
  const handleButtonClick = t3;
  if (!mounted || isSessionPending || !enabled || session?.user && isLoadingStatus) {
    let t4;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Button variant="outline" disabled={true} aria-label="Track domain"><IconBellPlus className="sm:text-muted-foreground" aria-hidden="true" /><span className="hidden sm:inline">Track</span></Button>;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  if (isTracked && isVerified) {
    let t4;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <TrackedVerifiedButton />;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    return t4;
  }
  let t4;
  if ($[12] !== handleButtonClick || $[13] !== isAuthenticated || $[14] !== isNavigating || $[15] !== isPendingVerification) {
    t4 = <TrackOrVerifyButton isPendingVerification={isPendingVerification} isAuthenticated={isAuthenticated} isNavigating={isNavigating} onClick={handleButtonClick} />;
    $[12] = handleButtonClick;
    $[13] = isAuthenticated;
    $[14] = isNavigating;
    $[15] = isPendingVerification;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}